import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileClock,
  Globe2,
  MessageSquareText,
  PlugZap,
  RefreshCcw,
  WalletCards,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { getApiErrorMessage } from "@/lib/apiError";

const CARD_DEFINITIONS = [
  {
    key: "trove",
    title: "Website / Trove",
    icon: Globe2,
    purpose:
      "Receive website leads from easyhomesource.com / Trove, including source page, interested home, UTM/campaign, and consent signals.",
    nextSteps: [
      "Public lead capture endpoint",
      "Website form webhook",
      "Source tracking",
      "Consent tracking",
      "Lead auto-assignment",
    ],
  },
  {
    key: "twilio",
    title: "Twilio",
    icon: MessageSquareText,
    purpose:
      "Send and receive SMS from lead/customer records, track opt-in/opt-out, and store message history when texting is enabled later.",
    nextSteps: [
      "Outbound SMS from lead/customer",
      "Inbound SMS webhook",
      "STOP/START handling",
      "Message history",
      "Opt-in enforcement",
    ],
  },
  {
    key: "quickbooks",
    title: "QuickBooks Online",
    icon: WalletCards,
    purpose:
      "Sync customers and invoices to QuickBooks while tracking QuickBooks customer IDs, invoice IDs, sync status, and errors.",
    nextSteps: [
      "OAuth connection",
      "Customer sync",
      "Draft invoice creation",
      "Payment/status sync later",
      "Sync error handling",
    ],
  },
  {
    key: "sync_logs",
    title: "Sync Logs",
    icon: FileClock,
    purpose:
      "Show integration activity, errors, and recent sync attempts across website lead capture, texting, accounting, and future systems.",
    nextSteps: [
      "Filter by provider",
      "Filter by status",
      "Review recent sync attempts",
      "Keep raw payloads and secrets out of logs",
    ],
  },
];

function StatusBadge({ configured, status }) {
  const ok = configured || status === "configured" || status === "healthy";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
        ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
      }`}
    >
      {ok ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : <Clock3 className="mr-1 h-3.5 w-3.5" />}
      {ok ? "Configured" : "Not configured"}
    </span>
  );
}

function IntegrationCard({ definition, health }) {
  const Icon = definition.icon;
  const missing = health?.missing || [];
  const lastSuccess = health?.last_success_at;
  const lastError = health?.last_error_at;

  return (
    <Card className="rounded-xl border-slate-200 p-5" data-testid={`integration-card-${definition.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(67,161,215,0.12)] text-[#0F2A47]">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{definition.title}</h2>
            {definition.key !== "sync_logs" ? (
              <p className="text-xs text-slate-500">{health?.status || "not_configured"}</p>
            ) : (
              <p className="text-xs text-slate-500">Recent integration activity</p>
            )}
          </div>
        </div>
        {definition.key !== "sync_logs" ? (
          <StatusBadge configured={health?.configured} status={health?.status} />
        ) : null}
      </div>

      <p className="mt-4 text-sm text-slate-600">{definition.purpose}</p>

      {definition.key !== "sync_logs" ? (
        <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm">
          <div className="font-medium text-slate-800">Connection Health</div>
          {missing.length ? (
            <div className="mt-2 text-slate-600">
              <div className="text-xs uppercase tracking-wide text-slate-500">Missing setup items</div>
              <ul className="mt-1 list-disc pl-5 font-mono text-xs">
                {missing.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="mt-2 text-sm text-emerald-700">Required environment variables are present.</div>
          )}
          <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
            <div>Last success: {lastSuccess ? formatDateTime(lastSuccess) : "None yet"}</div>
            <div>Last error: {lastError ? formatDateTime(lastError) : "None yet"}</div>
          </div>
          {health?.last_error_message ? (
            <div className="mt-2 rounded border border-red-100 bg-red-50 p-2 text-xs text-red-700">
              {health.last_error_message}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4">
        <div className="text-sm font-medium text-slate-800">Future placeholders</div>
        <ul className="mt-2 grid gap-1 text-sm text-slate-600 sm:grid-cols-2">
          {definition.nextSteps.map((step) => (
            <li key={step} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#43a1d7]" />
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

export default function Integrations() {
  const [health, setHealth] = useState({});
  const [events, setEvents] = useState([]);
  const [providerFilter, setProviderFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 25 };
      if (providerFilter !== "all") params.provider = providerFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [healthResponse, eventsResponse] = await Promise.all([
        api.get("/integrations/health"),
        api.get("/integrations/sync-events", { params }),
      ]);
      setHealth(healthResponse.data || {});
      setEvents(eventsResponse.data?.events || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Unable to load integrations"));
    } finally {
      setLoading(false);
    }
  }, [providerFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(() => CARD_DEFINITIONS, []);

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0F2A47] text-white">
              <PlugZap className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl text-slate-900">Integrations</h1>
              <p className="text-sm text-slate-500 mt-1">
                Connect EHS Command Center with website leads, SMS messaging, accounting, and external systems.
              </p>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="integrations-refresh-button">
          <RefreshCcw className="mr-2 h-4 w-4" /> {loading ? "Refreshing" : "Refresh"}
        </Button>
      </div>

      <Card className="flex gap-3 rounded-xl border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <div className="font-semibold">Secrets are managed in Render environment variables and are never shown here.</div>
          <div className="mt-1 text-amber-700">
            This page is foundation only: it does not send SMS, create public webhooks, or sync accounting records. Use Admin → Integrations → QuickBooks for the OAuth connection foundation.
          </div>
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        {cards.map((definition) => (
          <IntegrationCard key={definition.key} definition={definition} health={health[definition.key]} />
        ))}
      </div>

      <Card className="rounded-xl border-slate-200 p-5" data-testid="integration-sync-logs">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Sync Logs</h2>
            <p className="text-sm text-slate-500">Recent safe integration activity and placeholder log events.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="integrations-provider-filter"
            >
              <option value="all">All providers</option>
              <option value="trove">Website / Trove</option>
              <option value="twilio">Twilio</option>
              <option value="quickbooks">QuickBooks</option>
              <option value="website">Website</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="integrations-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
          <div className="hidden grid-cols-[1fr_1fr_1fr_1.2fr_1.6fr] gap-3 bg-slate-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 md:grid">
            <div>Created</div>
            <div>Provider</div>
            <div>Status</div>
            <div>Event</div>
            <div>Message</div>
          </div>
          {events.map((event) => (
            <div key={event.id || `${event.provider}-${event.created_at}`} className="grid gap-2 border-t border-slate-100 px-3 py-3 text-sm md:grid-cols-[1fr_1fr_1fr_1.2fr_1.6fr] md:gap-3">
              <div className="text-slate-500">{event.created_at ? formatDateTime(event.created_at) : "—"}</div>
              <div className="font-medium capitalize text-slate-800">{event.provider || "—"}</div>
              <div className="capitalize text-slate-700">{event.status || "—"}</div>
              <div className="text-slate-700">{event.event_type || event.resource_type || "—"}</div>
              <div className="text-slate-600">{event.message || "No message"}</div>
            </div>
          ))}
          {!events.length ? (
            <div className="border-t border-slate-100 px-3 py-8 text-center text-sm text-slate-500">
              No sync events yet. Future Trove, Twilio, and QuickBooks work will write safe events here.
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
